import { Inject, Injectable, InjectionToken, Optional } from "@angular/core";
import { AxiosApiClient, InternationalizationApi } from "@smallstack/axios-api-client";
import { Logger } from "@smallstack/core-common";
import { BehaviorSubject } from "rxjs";
import { loadAngularLocaleDataAsync } from "../utils/angular-loader";
import { TranslationLoader } from "../utils/translation-loader";
import * as i0 from "@angular/core";
import * as i1 from "../utils/translation-loader";
import * as i2 from "@smallstack/axios-api-client";
import * as i3 from "rxjs";
export const currentLocale$ = new BehaviorSubject("de_de");
export const CURRENT_LOCALE$ = new InjectionToken("currentLocale");
export const fallbackChains = {
    de_de: ["de_de", "en_us"]
};
export const FALLBACK_CHAINS = new InjectionToken("fallBackChains");
export class LocaleService {
    constructor(translationLoader, axiosApiClient, currentLocale$, fallbackChains) {
        this.translationLoader = translationLoader;
        this.axiosApiClient = axiosApiClient;
        this.currentLocale$ = currentLocale$;
        this.fallbackChains = fallbackChains;
        this.availableLocales$ = new BehaviorSubject(undefined);
        this.fallbackLocale = "de_de";
    }
    getCurrentLocale() {
        if (this.currentLocale$.value !== undefined)
            return this.currentLocale$.value;
        return this.fallbackLocale;
    }
    async setCurrentLocale(locale) {
        // check locale first
        if (!new RegExp("^[a-z]{2}_[a-z]{2}$").test(locale))
            return Promise.reject(new Error("not a valid locale: " + locale));
        await loadAngularLocaleDataAsync(locale);
        await this.translationLoader.load(locale);
        localStorage.setItem("locale", locale);
        if (this.currentLocale$ === undefined)
            return Promise.reject(new Error("currentLocale$ is still undefined"));
        this.currentLocale$.next(locale);
    }
    getDefaultLocale() {
        const allLocales = this.availableLocales$.value;
        return ((allLocales &&
            allLocales
                .filter((locale) => locale.active === true)
                .sort((localeA, localeB) => localeA.index - localeB.index)[0]?.iso) ||
            this.fallbackLocale);
    }
    async getAllLocales() {
        if (this.axiosApiClient) {
            const i18nApi = this.axiosApiClient.get(InternationalizationApi);
            if (this.availableLocales$.value === undefined) {
                const localesPage = (await i18nApi.getLocales({ size: 1000 })).data;
                this.availableLocales$.next(localesPage.elements);
            }
            if (this.availableLocales$.value instanceof Array) {
                const fallbackChains = (await i18nApi.getLocaleFallbackChains({})).data;
                for (const item of fallbackChains)
                    this.fallbackChains[item.locale] = item.fallbackChain;
            }
        }
        else {
            Logger.warning("LocaleService", "Please provide an axiosApiClient in your Angular Module if you want to get all locales via localeService.getAllLocales!");
        }
        return this.availableLocales$.value;
    }
    getFallbackChain(locale) {
        return this.fallbackChains[locale];
    }
    addFallbackChain(locale, chain) {
        if (!chain || !locale)
            return;
        if (chain[0] !== locale)
            throw new Error("Locale chains should always start with the locale itself");
        this.fallbackChains[locale] = chain;
    }
    async detectLocale() {
        const allLocales = this.availableLocales$.value;
        if (!allLocales)
            throw new Error("please call localeService.getAllLocales() first!");
        // check local storage
        const lsLang = localStorage.getItem("locale");
        if (lsLang && typeof lsLang === "string") {
            if (allLocales.find((l) => l.iso === lsLang) !== undefined) {
                await this.setCurrentLocale(lsLang);
                return;
            }
            else
                await this.setCurrentLocale(undefined); // delete current locale if it could not be found
        }
        // browser locale
        if ("language" in navigator) {
            let language = navigator.language;
            if (!language)
                return;
            language = language.replace("-", "_").toLocaleLowerCase();
            let locale;
            if (isBrowserLocale(language))
                locale = language;
            else if (isBrowserLanguage(language))
                locale = this.getLocaleForLanguage(language);
            if (allLocales.find((l) => l.iso === locale)) {
                await this.setCurrentLocale(locale);
                return;
            }
        }
        // use default locale, but don't persist in local storage
        this.currentLocale$.next(this.getDefaultLocale());
    }
    getLocaleForLanguage(language) {
        // TODO: how...
        switch (language) {
            case "en":
                return "en_us";
            default:
                return language + "_" + language;
        }
    }
}
LocaleService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.5", ngImport: i0, type: LocaleService, deps: [{ token: i1.TranslationLoader }, { token: i2.AxiosApiClient, optional: true }, { token: CURRENT_LOCALE$ }, { token: FALLBACK_CHAINS }], target: i0.ɵɵFactoryTarget.Injectable });
LocaleService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.5", ngImport: i0, type: LocaleService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.5", ngImport: i0, type: LocaleService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.TranslationLoader }, { type: i2.AxiosApiClient, decorators: [{
                    type: Optional
                }] }, { type: i3.BehaviorSubject, decorators: [{
                    type: Inject,
                    args: [CURRENT_LOCALE$]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [FALLBACK_CHAINS]
                }] }]; } });
function isBrowserLocale(value) {
    return /[a-z]{2}_[a-z]{2}/.test(value);
}
function isBrowserLanguage(value) {
    return /[a-z]{2}/.test(value);
}
